import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { SecureMuiSubmitButton } from '@components/buttons/SecureMuiSubmitButton';
import { Button as CommonBtn } from '@components/index';
import { fonts } from '@theme/fontsCustomer';
import { EditEmailIcon, EyeIcon, EyeSplashIcon } from '@icons/index';
import { OAuthButtons } from './OAuthButtons';
import { useSignForm } from './validationSchema';

// 애니메이션 전환 상수
const TRANSITION_DURATION = '0.65s';
const TRANSITION_EASING = 'cubic-bezier(0.25, 0.1, 0.25, 1)';

// 각 섹션의 max-height (애니메이션용, 실제 높이보다 충분히 큰 값)
const CONTINUE_BTN_MAX_HEIGHT = '70px';
const SSO_ONLY_SECTION_MAX_HEIGHT = '60px';
const PASSWORD_SECTION_MAX_HEIGHT = '300px';
const OAUTH_BUTTONS_MAX_HEIGHT = '220px';

type RememberEmailCheckboxProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
};

/** 아이디 기억하기 체크박스 (SsoOnlySection, PasswordSection 공용) */
const RememberEmailCheckbox = ({ checked, onChange }: RememberEmailCheckboxProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <FormControlLabel
      control={
        <Checkbox
          size="small"
          checked={checked}
          onChange={e => onChange(e.target.checked)}
          sx={{
            padding: '0 6px 0 0',
            color: theme.colors['border-gray-light'],
            '&.Mui-checked': {
              color: theme.colors['bg-primary'],
            },
          }}
        />
      }
      label={
        <Typography component="span" variant="button4">
          {t('Login.RememberEmail')}
        </Typography>
      }
      sx={{
        margin: 0,
        '&:hover .MuiTypography-root': { color: theme.colors['state-primary-hover'] },
      }}
    />
  );
};

type Props = {
  email: string;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
  onBack: () => void;
  onSubmit: (data: { username: string; password: string }) => void;
  isPasswordStep: boolean;
  isLookupPending: boolean;
  isLoginPending: boolean;
  isAuthenticationFailed: boolean;
  loginErrorMessage: string | null;
  invalidTenant: boolean;
  showGoogleLogin: boolean;
  showMicrosoftLogin: boolean;
  /** 비밀번호 로그인 비활성화 환경 (KTDS 등): SSO만 노출 */
  disablePasswordLogin: boolean;
  /** 최초 화면에 SSO 버튼만 노출 (이메일 입력 없이 바로 SSO 버튼 표시) */
  showSsoAsDefault: boolean;
  onGoogleLogin: () => void;
  onMicrosoftLogin: () => void;
  rememberEmail: boolean;
  onRememberEmailChange: (checked: boolean) => void;
  isItAdminPage?: boolean;
};

/**
 * 통합 로그인 컴포넌트
 * - EMAIL_INPUT: 이메일 입력 + "다음" 버튼
 * - PASSWORD_INPUT: 이메일(disabled) + 비밀번호 dropdown + "로그인" 버튼
 */
export const LoginStep = ({
  email,
  onEmailChange,
  onContinue,
  onBack,
  onSubmit,
  isPasswordStep,
  isLookupPending,
  isLoginPending,
  isAuthenticationFailed,
  loginErrorMessage,
  invalidTenant,
  showGoogleLogin,
  showMicrosoftLogin,
  disablePasswordLogin,
  showSsoAsDefault,
  onGoogleLogin,
  onMicrosoftLogin,
  rememberEmail,
  onRememberEmailChange,
  isItAdminPage,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useSignForm();

  const passwordValue = watch('password');

  useEffect(() => {
    setValue('username', email);
  }, [email, setValue]);

  // 비밀번호 단계 진입 시 포커스
  useEffect(() => {
    if (!isPasswordStep) return undefined;

    const ANIMATION_SETTLE_MS = 500;
    const timer = setTimeout(() => {
      passwordRef.current?.focus();
    }, ANIMATION_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [isPasswordStep]);

  const handleEmailKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onContinue();
    }
  };

  const handleClickShowPassword = () => setShowPassword(show => !show);

  const handleClickSearchPassword = () => {
    navigate('/search-password', { replace: true });
  };

  const handleIntroduceInquiryClick = () => {
    navigate('/tenant-request');
  };

  const passwordHelperText =
    errors.password?.message ||
    (isAuthenticationFailed && (loginErrorMessage || t('Account_Validation_Check_01'))) ||
    (invalidTenant && t('Sign_In.Access_Denied')) ||
    undefined;

  // SSO 기본 노출 모드: 이메일 폼 없이 SSO 버튼만 표시
  if (showSsoAsDefault) {
    return (
      <Container $isItAdminPage={isItAdminPage}>
        <ContentWrapper>
          <LoginTitleWrapper>
            <Typography variant="headline3">{t('GNB_Menu_05')}</Typography>
          </LoginTitleWrapper>
          <OAuthButtons
            showGoogleLogin={showGoogleLogin}
            showMicrosoftLogin={showMicrosoftLogin}
            hideDivider
            onGoogleLogin={onGoogleLogin}
            onMicrosoftLogin={onMicrosoftLogin}
          />
          <QuestionsWrapper>
            {!isItAdminPage && (
              <Question>
                <span>{t('Login_String_02')}</span>
                <TextButton size="small" variant="text" onClick={handleIntroduceInquiryClick}>
                  {t('Sign_In_03')}
                </TextButton>
              </Question>
            )}
          </QuestionsWrapper>
        </ContentWrapper>
      </Container>
    );
  }

  return (
    <Container $isItAdminPage={isItAdminPage}>
      <ContentWrapper>
        <LoginTitleWrapper>
          <Typography variant="headline3">{t('GNB_Menu_05')}</Typography>
        </LoginTitleWrapper>

        <WrapperForm onSubmit={handleSubmit(onSubmit)}>
          {/* 이메일 입력 */}
          <EmailInput
            placeholder={t('SsoProvider.Login.EmailPlaceholder')}
            value={email}
            onChange={e => {
              onEmailChange(e.target.value);
              setValue('username', e.target.value);
            }}
            onKeyDown={!isPasswordStep ? handleEmailKeyDown : undefined}
            autoFocus={!isPasswordStep}
            disabled={isPasswordStep || isLookupPending}
            fullWidth
            $isDisabled={isPasswordStep}
            helperText={errors.username?.message}
            error={!!errors.username?.message}
            InputProps={{
              startAdornment: !isPasswordStep ? (
                <InputAdornment position="start">
                  <AtSymbol>@</AtSymbol>
                </InputAdornment>
              ) : undefined,
              endAdornment: isPasswordStep ? (
                <InputAdornment position="end">
                  <EditEmailButton aria-label="이메일 변경" onClick={onBack}>
                    <EditEmailIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                  </EditEmailButton>
                </InputAdornment>
              ) : undefined,
            }}
          />

          {/* "다음" 버튼 - 비밀번호 단계에서 접힘 */}
          <ContinueButtonWrapper $visible={!isPasswordStep}>
            <ContinueButton
              type="button"
              onClick={onContinue}
              disabled={!email.trim() || isLookupPending}
              tabIndex={isPasswordStep ? -1 : 0}
            >
              {isLookupPending ? <CircularProgress size={16} color="inherit" /> : t('Common.next')}
            </ContinueButton>
          </ContinueButtonWrapper>

          {/* 비밀번호 비활성화 환경: 아이디 기억하기만 dropdown animation으로 표시 */}
          {disablePasswordLogin && (
            <SsoOnlySection $visible={isPasswordStep}>
              <RememberEmailCheckbox checked={rememberEmail} onChange={onRememberEmailChange} />
            </SsoOnlySection>
          )}

          {/* 비밀번호 섹션 - dropdown animation (비밀번호 비활성화 환경에서는 미표시) */}
          {!disablePasswordLogin && (
            <PasswordSection $visible={isPasswordStep}>
              <PasswordSectionContent>
                <PasswordField
                  {...register('password', {
                    required: t('Account_Validation_Check_03'),
                  })}
                  inputRef={passwordRef}
                  size="large"
                  fullWidth
                  placeholder={t('GNB_Profile_MyProfile_07')}
                  type={showPassword ? 'text' : 'password'}
                  helperText={passwordHelperText}
                  error={!!errors.password?.message || isAuthenticationFailed}
                  InputProps={{
                    endAdornment: (
                      <PasswordToggleButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        className={passwordValue.length === 0 ? 'hide-toggle-btn' : undefined}
                        edge="end"
                      >
                        {showPassword ? (
                          <EyeIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                        ) : (
                          <EyeSplashIcon width={20} height={20} color={theme.colors['ic-gray-light']} />
                        )}
                      </PasswordToggleButton>
                    ),
                  }}
                />

                <OptionsRow>
                  <RememberEmailCheckbox checked={rememberEmail} onChange={onRememberEmailChange} />
                  <Button
                    variant="text"
                    onClick={handleClickSearchPassword}
                    sx={{
                      padding: 0,
                      height: 'auto',
                      minWidth: 'auto',
                      '& .MuiTouchRipple-root': { display: 'none' },
                      '&:hover span': { color: theme.colors['state-primary-hover'] },
                    }}
                  >
                    <Typography component="span" variant="button4">
                      {t('GNB_Profile_MyProfile_09')}
                    </Typography>
                  </Button>
                </OptionsRow>

                <LoginButton variant="contained" size="2extraLarge" type="submit" isPending={isLoginPending}>
                  {t('GNB_Menu_05')}
                </LoginButton>
              </PasswordSectionContent>
            </PasswordSection>
          )}
        </WrapperForm>

        {/* SSO 버튼: 이메일 입력 후 다음 단계에서만 표시 (이메일 입력 단계에서는 숨김) */}
        {(showGoogleLogin || showMicrosoftLogin) && (
          <OAuthButtonsWrapper $visible={isPasswordStep}>
            <OAuthButtons
              showGoogleLogin={showGoogleLogin}
              showMicrosoftLogin={showMicrosoftLogin}
              hideDivider={disablePasswordLogin}
              onGoogleLogin={onGoogleLogin}
              onMicrosoftLogin={onMicrosoftLogin}
            />
          </OAuthButtonsWrapper>
        )}

        <QuestionsWrapper>
          {!isItAdminPage && (
            <Question>
              <span>{t('Login_String_02')}</span>
              <TextButton size="small" variant="text" onClick={handleIntroduceInquiryClick}>
                {t('Sign_In_03')}
              </TextButton>
            </Question>
          )}
        </QuestionsWrapper>
      </ContentWrapper>
    </Container>
  );
};

const Container = styled.div<{ $isItAdminPage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: ${({ $isItAdminPage }) => ($isItAdminPage ? '194px' : '70px')} 0px 0px 0px;
  min-height: calc(100vh - 70px - var(--employee-gnb-height));
  height: 100%;
  background-color: ${({ theme }) => theme.colors['bg-white']};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 380px;
`;

const LoginTitleWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 40px;
`;

const WrapperForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const AtSymbol = styled.span`
  ${fonts.Body2};
  color: ${({ theme }) => theme.colors['text-gray-light']};
  font-weight: 500;
`;

const EmailInput = styled(TextField)<{ $isDisabled?: boolean }>`
  & .MuiInputBase-root {
    height: 54px;
    border-radius: 5px;
    background-color: ${({ $isDisabled }) => ($isDisabled ? '#EBEBEB' : 'transparent')};
    transition: background-color 0.5s ease;
  }
  & .MuiInputBase-input {
    ${fonts.Body1};
    background-color: transparent !important;
    padding-left: 12px;
    -webkit-text-fill-color: ${({ $isDisabled, theme }) => ($isDisabled ? theme.colors['text-gray-main'] : 'inherit')};
  }
  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.colors['text-gray-light']};
  }
  & .MuiOutlinedInput-notchedOutline {
    background-color: transparent !important;
  }
  & .MuiInputBase-input:-webkit-autofill {
    -webkit-box-shadow: ${({ $isDisabled }) =>
      $isDisabled ? '0 0 0 1000px #EBEBEB inset' : '0 0 0 1000px #ffffff inset'} !important;
  }
`;

const EditEmailButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  padding: 0;
  margin-right: 8px;
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: none;
    svg path {
      fill: ${({ theme }) => theme.colors['ic-primary-light']};
    }
  }
`;

const ContinueButtonWrapper = styled.div<{ $visible: boolean }>`
  width: 100%;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? CONTINUE_BTN_MAX_HEIGHT : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  margin-top: ${({ $visible }) => ($visible ? '10px' : '0')};
  transition: max-height ${TRANSITION_DURATION} ${TRANSITION_EASING}, opacity ${TRANSITION_DURATION} ${TRANSITION_EASING},
    margin-top ${TRANSITION_DURATION} ${TRANSITION_EASING};
`;

const ContinueButton = styled.button`
  width: 100%;
  height: 54px;
  border: none;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  color: ${({ theme }) => theme.colors['text-white']};
  ${fonts.Button2};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }

  &:disabled {
    opacity: 0.5;
    cursor: auto;
  }
`;

// SSO 버튼 영역 - 이메일 입력 단계에서는 숨기고 다음 단계에서 슬라이드 인
const OAuthButtonsWrapper = styled.div<{ $visible: boolean }>`
  width: calc(100% + 4px);
  margin-left: -2px;
  padding: 0 2px;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? OAUTH_BUTTONS_MAX_HEIGHT : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  transition: max-height ${TRANSITION_DURATION} ${TRANSITION_EASING}, opacity ${TRANSITION_DURATION} ${TRANSITION_EASING},
    transform ${TRANSITION_DURATION} ${TRANSITION_EASING};
`;

// 비밀번호 비활성화 환경에서 아이디 기억하기를 애니메이션으로 표시
const SsoOnlySection = styled.div<{ $visible: boolean }>`
  width: 100%;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? SSO_ONLY_SECTION_MAX_HEIGHT : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  margin-top: ${({ $visible }) => ($visible ? '14px' : '0')};
  transition: max-height ${TRANSITION_DURATION} ${TRANSITION_EASING}, opacity ${TRANSITION_DURATION} ${TRANSITION_EASING},
    margin-top ${TRANSITION_DURATION} ${TRANSITION_EASING};
`;

const PasswordSection = styled.div<{ $visible: boolean }>`
  width: 100%;
  overflow: hidden;
  max-height: ${({ $visible }) => ($visible ? PASSWORD_SECTION_MAX_HEIGHT : '0')};
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: translateY(${({ $visible }) => ($visible ? '0' : '-8px')});
  transition: max-height ${TRANSITION_DURATION} ${TRANSITION_EASING}, opacity ${TRANSITION_DURATION} ${TRANSITION_EASING},
    transform ${TRANSITION_DURATION} ${TRANSITION_EASING};
`;

const PasswordSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 10px;
`;

const PasswordField = styled(TextField)`
  & .MuiInputBase-input::placeholder {
    ${({ theme }) => theme.colors['text-gray-sub-dark']};
    ${fonts.Body1};
  }

  & .hide-toggle-btn {
    display: none;
  }
`;

const OptionsRow = styled(Box)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 14px;
  margin-bottom: 30px;
`;

const LoginButton = styled(SecureMuiSubmitButton)`
  width: 100%;
  box-shadow: none;
  border: none;
  padding: 15px 24px;
  background: ${({ theme }) => theme.colors['bg-gradient']};
  color: ${({ theme }) => theme.colors['text-white']};
  &:hover {
    box-shadow: none;
    border: none;
    background: ${({ theme }) => theme.colors['state-gradient-hover']};
  }
`;

const PasswordToggleButton = styled(IconButton)`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 8px;
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: none;
    svg path {
      fill: ${({ theme }) => theme.colors['ic-primary-light']};
    }
  }
`;

const QuestionsWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-top: 40px;
`;

const Question = styled(Box)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${fonts.Headline8};
  color: ${props => props.theme.colors['text-gray-sub-dark']};
`;

const TextButton = styled(CommonBtn)`
  height: 20px;
  padding: 0;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors['text-primary-light']};
  & .MuiTouchRipple-root {
    display: none;
  }
  &:hover {
    background: none;
  }
`;
